import type { ExerciseTemplate } from '@yuan-math/exercises';
import { fraction, roundTo, toLatex, toText } from '@yuan-math/math';
import {
  choiceExercise,
  expressionExercise,
  fractionExercise,
  numericExercise,
  options,
  template,
  trap,
  within,
} from './authoring.ts';

/** The largest perfect square dividing n, for pulling factors out of a root. */
const largestSquareFactor = (n: number): number => {
  let best = 1;
  for (let k = 2; k * k <= n; k += 1) if (n % (k * k) === 0) best = k * k;
  return best;
};

/** Radicals, exponential change, sequences, rational expressions, and absolute value equations and inequalities. */
export const ALGEBRA_ONE_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'radical-simplify',
    conceptId: 'radicals',
    description: 'Pull the largest perfect square out of a radical.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const outside = rng.int(2, difficulty === 4 ? 7 : 4);
      const inside = rng.pick([2, 3, 5, 6, 7, 10, 11, 13, 15]);
      const radicand = outside * outside * inside;

      return numericExercise({
        prompt: `Simplify √${radicand} to the form a√b, where b has no square factors. What is a?`,
        latex: `\\sqrt{${radicand}}`,
        value: outside,
        summary: `${radicand} = ${outside * outside} × ${inside}, and √${outside * outside} = ${outside}, so √${radicand} = ${outside}√${inside}.`,
        hint: 'Find the largest perfect square that divides the number under the root.',
        steps: [
          { label: 'Split off the largest square factor.', latex: `\\sqrt{${outside * outside} \\times ${inside}}` },
          { label: 'Take the root of the square part.', latex: `${outside}\\sqrt{${inside}}` },
        ],
        traps: [
          // A smaller square pulled out, leaving one still inside.
          ...(largestSquareFactor(radicand) !== outside * outside || outside === 1
            ? []
            : [trap(1, 'radical-not-fully-simplified')]),
        ],
      });
    },
  }),

  template({
    id: 'radical-add-like',
    conceptId: 'radicals',
    description: 'Add radicals, where only like ones combine.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);
      const inside = rng.pick([2, 3, 5, 6, 7, 11]);

      return numericExercise({
        prompt: `Simplify ${a}√${inside} + ${b}√${inside} to the form c√${inside}. What is c?`,
        latex: `${a}\\sqrt{${inside}} + ${b}\\sqrt{${inside}}`,
        value: a + b,
        summary: `They are like radicals, so the coefficients add: ${a} + ${b} = ${a + b}, giving ${a + b}√${inside}.`,
        hint: 'Treat √' + inside + ' the way you would treat x. How many of them are there altogether?',
        steps: [
          { label: 'The radicals match, so they collect like terms.', latex: `(${a} + ${b})\\sqrt{${inside}}` },
          { label: 'Add the coefficients.', latex: `${a + b}\\sqrt{${inside}}` },
        ],
        // Multiplying the radicands, or adding under one root.
        traps: [...(a * b === a + b ? [] : [trap(a * b, 'radical-added-termwise')])],
      });
    },
  }),

  template({
    id: 'radical-add-unlike',
    conceptId: 'radicals',
    description: 'Recognise that unlike radicals do not combine.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const first = rng.pick([2, 3, 5]);
      const second = rng.pick([6, 7, 10, 11].filter((n) => n !== first));

      const { choices, correctId } = options(
        [
          { latex: `\\sqrt{${first}} + \\sqrt{${second}}`, correct: true },
          { latex: `\\sqrt{${first + second}}`, misconceptionId: 'radical-added-termwise' },
          { latex: `2\\sqrt{${first + second}}`, misconceptionId: 'radical-added-termwise' },
          { latex: `\\sqrt{${first * second}}`, misconceptionId: 'radical-added-termwise' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `What is √${first} + √${second} in its simplest form?`,
        choices,
        correctId,
        summary: `√${first} ≈ ${roundTo(Math.sqrt(first), 3)} and √${second} ≈ ${roundTo(Math.sqrt(second), 3)}, which add to about ${roundTo(Math.sqrt(first) + Math.sqrt(second), 3)} — while √${first + second} ≈ ${roundTo(Math.sqrt(first + second), 3)}. They are different numbers, so the expression cannot be combined.`,
        hint: 'Check with a calculator whether any of these actually equal the original. Roots do not add across.',
      });
    },
  }),

  template({
    id: 'radical-multiply',
    conceptId: 'radicals',
    description: 'Multiply two radicals, where the product turns out to be whole.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      /*
       * Built as p·u² and p·v² with p squarefree, so the product p²u²v² is a
       * perfect square while neither factor is one. That guarantees both
       * radicals are genuinely irrational and the answer is genuinely whole —
       * the case worth practising, because it is where a student sees two
       * irrational numbers multiply to an integer, and where forgetting the
       * final root is most visible.
       *
       * Picking the answer first and then splitting its square would have been
       * the obvious construction and is wrong: 25 has no usable split at all,
       * and the fallback silently produced √2 × √12.5.
       */
      const p = rng.pick([2, 3, 5, 6, 7, 10]);
      const u = rng.int(1, difficulty === 4 ? 3 : 2);
      // Distinct, so the two radicands differ and the question is not √a × √a.
      const v = rng.pick([1, 2, 3, 4].filter((n) => n !== u).slice(0, difficulty === 4 ? 3 : 2));
      const first = p * u * u;
      const second = p * v * v;
      const root = p * u * v;

      return numericExercise({
        prompt: `What is √${first} × √${second}?`,
        latex: `\\sqrt{${first}} \\times \\sqrt{${second}}`,
        value: root,
        summary: `A root splits across multiplication, so √${first} × √${second} = √${first * second} = ${root}. Two irrational numbers, and a whole one out the other side.`,
        hint: 'Multiplying under the root keeps it under the root. What is the product, and what is its square root?',
        steps: [
          { label: 'Combine under one root.', latex: `\\sqrt{${first} \\times ${second}}` },
          { label: 'Multiply.', latex: `\\sqrt{${first * second}}` },
          { label: 'Take the root.', latex: `${root}` },
        ],
        traps: [
          // The radicands multiplied and the root never taken off.
          trap(first * second, 'radical-product-not-rooted'),
          // The radicands added instead of multiplied.
          ...(first + second === root || first + second === first * second
            ? []
            : [trap(first + second, 'radical-added-termwise')]),
        ],
      });
    },
  }),

  template({
    id: 'exponential-growth-value',
    conceptId: 'exponential-functions',
    description: 'Apply repeated multiplicative growth.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const start = rng.int(1, 20) * 100;
      const rate = rng.pick([10, 20, 25, 50]);
      const periods = rng.int(2, difficulty === 4 ? 5 : 3);
      const factor = 1 + rate / 100;
      const answer = roundTo(start * factor ** periods, 2);
      const linear = roundTo(start + (start * rate * periods) / 100, 2);

      return numericExercise({
        prompt: `A population of ${start} grows by ${rate}% each year. What is it after ${periods} years?`,
        value: answer,
        tolerance: within(0.005, answer),
        summary: `Each year multiplies by ${factor}, so after ${periods} years it is ${start} × ${factor}^${periods} = ${answer}.`,
        hint: 'The growth is applied to the new total each year, not to the original.',
        steps: [
          { label: 'Find the multiplier for one year.', latex: `1 + \\frac{${rate}}{100} = ${factor}` },
          { label: 'Apply it once per year.', latex: `${start} \\times ${factor}^{${periods}}` },
          { label: 'Work it out.', latex: `${answer}` },
        ],
        // Growth applied to the original each year — that would be linear.
        traps: [...(linear === answer ? [] : [trap(linear, 'exponential-treated-as-linear')])],
      });
    },
  }),

  template({
    id: 'exponential-or-linear',
    conceptId: 'exponential-functions',
    description: 'Tell exponential change from linear change in a table.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const exponential = rng.bool();
      const start = rng.int(2, 6);
      const step = rng.int(2, 4);
      const values = exponential
        ? [start, start * step, start * step * step, start * step ** 3]
        : [start, start + step, start + 2 * step, start + 3 * step];

      const { choices, correctId } = options(
        [
          {
            label: exponential ? `Exponential — each term is ${step} times the one before` : `Linear — each term is ${step} more than the one before`,
            correct: true,
          },
          {
            label: exponential ? `Linear — each term is ${step} more than the one before` : `Exponential — each term is ${step} times the one before`,
            misconceptionId: 'exponential-treated-as-linear',
          },
          { label: 'Neither — there is no pattern', misconceptionId: 'sequence-rule-confused' },
          { label: 'Both, since any pattern can be written either way', misconceptionId: 'exponential-treated-as-linear' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `A quantity takes the values ${values.join(', ')}. How is it changing?`,
        choices,
        correctId,
        summary: exponential
          ? `Dividing each term by the one before gives ${step} every time, so the growth is multiplicative.`
          : `Subtracting each term from the next gives ${step} every time, so the growth is additive.`,
        hint: 'Try both: subtract consecutive terms, then divide them. Whichever gives a constant is the rule.',
      });
    },
  }),

  template({
    id: 'sequence-next-term',
    conceptId: 'sequences',
    description: 'Continue an arithmetic or geometric sequence.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const geometric = rng.bool();
      const start = rng.int(2, difficulty === 3 ? 12 : 6);
      const step = rng.int(2, geometric ? 4 : 9);
      const terms = geometric
        ? [start, start * step, start * step ** 2, start * step ** 3]
        : [start, start + step, start + 2 * step, start + 3 * step];
      const answer = geometric ? start * step ** 4 : start + 4 * step;
      const other = geometric ? (terms[3] as number) + step : (terms[3] as number) * step;

      return numericExercise({
        prompt: `What is the next term of this sequence? ${terms.join(', ')}, …`,
        value: answer,
        summary: geometric
          ? `Each term is ${step} times the one before, so the next is ${terms[3]} × ${step} = ${answer}.`
          : `Each term is ${step} more than the one before, so the next is ${terms[3]} + ${step} = ${answer}.`,
        hint: 'Look at what takes you from one term to the next. Is it an addition or a multiplication?',
        steps: [
          {
            label: geometric ? 'Divide consecutive terms to find the common ratio.' : 'Subtract consecutive terms to find the common difference.',
            latex: `${step}`,
          },
          { label: 'Apply it once more.', latex: `${answer}` },
        ],
        traps: [...(other === answer ? [] : [trap(other, 'sequence-rule-confused')])],
      });
    },
  }),

  template({
    id: 'sequence-nth-term',
    conceptId: 'sequences',
    description: 'Use the rule for the nth term of an arithmetic sequence.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const start = rng.int(1, 12);
      const step = rng.int(2, 9);
      const n = rng.int(6, difficulty === 4 ? 40 : 15);
      const answer = start + (n - 1) * step;

      return numericExercise({
        prompt: `An arithmetic sequence starts at ${start} and goes up by ${step} each time. What is the ${n}th term?`,
        value: answer,
        summary: `The nth term is ${start} + (n − 1) × ${step}, so the ${n}th is ${start} + ${n - 1} × ${step} = ${answer}.`,
        hint: 'The first term has had no steps added yet, so the nth term has had n − 1 of them.',
        steps: [
          { label: 'Write the rule.', latex: `a_n = ${start} + (n - 1)(${step})` },
          { label: 'Substitute.', latex: `${start} + ${n - 1} \\times ${step} = ${answer}` },
        ],
        // The classic off-by-one: n steps instead of n − 1.
        traps: [trap(start + n * step, 'sequence-rule-confused')],
      });
    },
  }),

  template({
    id: 'sequence-geometric-nth',
    conceptId: 'sequences',
    description: 'Find a term of a geometric sequence, where the exponent is n − 1.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const start = rng.int(1, 6);
      const ratio = rng.int(2, difficulty === 4 ? 4 : 3);
      const n = rng.int(4, difficulty === 4 ? 8 : 6);
      const answer = start * ratio ** (n - 1);
      const terms = [0, 1, 2, 3].map((i) => start * ratio ** i);

      return numericExercise({
        prompt: `This sequence multiplies by ${ratio} each time: ${terms.join(', ')}, … What is its ${n}th term?`,
        value: answer,
        summary: `The nth term is ${start} × ${ratio}^(n − 1), because the first term has been multiplied zero times. So the ${n}th is ${start} × ${ratio}^${n - 1} = ${answer}.`,
        hint: 'How many times has the first term been multiplied by the ratio? Not n times — count again for the first term.',
        steps: [
          { label: 'Write the rule.', latex: `a_n = ${start} \\times ${ratio}^{\\,n - 1}` },
          { label: 'Substitute.', latex: `${start} \\times ${ratio}^{${n - 1}} = ${answer}` },
          { label: 'Check it on n = 1.', latex: `${start} \\times ${ratio}^{0} = ${start}` },
        ],
        traps: [
          // The exponent taken as n, so the ratio is applied once too often.
          trap(start * ratio ** n, 'geometric-term-index-off-by-one'),
          // Adding the ratio each time rather than multiplying by it.
          ...(start + (n - 1) * ratio === answer ? [] : [trap(start + (n - 1) * ratio, 'sequence-rule-confused')]),
        ],
      });
    },
  }),

  template({
    id: 'rational-simplify',
    conceptId: 'rational-expressions',
    description: 'Cancel a common factor, not a common term.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 8);
      const b = rng.int(1, 8) + a;

      const { choices, correctId } = options(
        [
          { latex: `\\frac{x + ${a}}{x + ${b}}`, correct: true },
          { latex: `\\frac{${a}}{${b}}`, misconceptionId: 'rational-terms-cancelled' },
          { latex: `\\frac{x}{x}`, misconceptionId: 'rational-terms-cancelled' },
          { latex: `${a - b}`, misconceptionId: 'rational-terms-cancelled' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these is this expression in its simplest form?',
        latex: `\\frac{x + ${a}}{x + ${b}}`,
        choices,
        correctId,
        summary: `Nothing cancels. The x is being *added*, not multiplied, so it is a term and not a factor — the expression is already as simple as it gets.`,
        hint: 'Only factors cancel. Is the x being multiplied, or added?',
      });
    },
  }),

  template({
    id: 'rational-simplify-factored',
    conceptId: 'rational-expressions',
    description: 'Factorise, then cancel the common bracket.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const p = rng.int(1, 7);
      let q = rng.int(1, 7);
      if (q === p) q = p + 1;
      const shared = rng.int(1, 7);
      // (x + shared)(x + p) over (x + shared)(x + q) cancels to (x + p)/(x + q).
      const numeratorMiddle = shared + p;
      const numeratorLast = shared * p;
      const denominatorMiddle = shared + q;
      const denominatorLast = shared * q;

      return expressionExercise({
        prompt: 'Simplify by factorising the top and the bottom, then cancelling.',
        latex: `\\frac{x^2 + ${numeratorMiddle}x + ${numeratorLast}}{x^2 + ${denominatorMiddle}x + ${denominatorLast}}`,
        canonical: `(x + ${p}) / (x + ${q})`,
        summary: `Both factorise with (x + ${shared}), which cancels: (x + ${p})/(x + ${q}).`,
        hint: 'Factorise both quadratics first. A bracket appearing on top and bottom is a factor, so it cancels.',
        steps: [
          { label: 'Factorise the numerator.', latex: `(x + ${shared})(x + ${p})` },
          { label: 'Factorise the denominator.', latex: `(x + ${shared})(x + ${q})` },
          { label: 'Cancel the shared factor.', latex: `\\frac{x + ${p}}{x + ${q}}` },
        ],
      });
    },
  }),

  template({
    id: 'absolute-value-solve',
    conceptId: 'absolute-value',
    description: 'Solve an absolute value equation, which has two answers.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const k = rng.int(2, difficulty === 3 ? 20 : 9);
      const shift = difficulty === 3 ? rng.int(1, 9) * (rng.bool() ? 1 : -1) : 0;
      // |x + shift| = k has solutions k - shift and -k - shift.
      const larger = k - shift;
      const smaller = -k - shift;

      return numericExercise({
        prompt: 'Solve this equation. Give the larger of the two solutions.',
        latex: shift === 0 ? `|x| = ${k}` : `|x ${shift < 0 ? '-' : '+'} ${Math.abs(shift)}| = ${k}`,
        value: larger,
        summary: `The inside can be ${k} or −${k}, giving x = ${larger} or x = ${smaller}. The larger is ${larger}.`,
        hint: 'Absolute value is distance from zero, so the expression inside can be positive or negative.',
        steps: [
          { label: 'The inside equals k or −k.', latex: `x ${shift < 0 ? '-' : '+'} ${Math.abs(shift)} = ${k} \\text{ or } ${-k}` },
          { label: 'Solve each.', latex: `x = ${larger} \\text{ or } x = ${smaller}` },
        ],
        traps: [trap(smaller, 'absolute-value-one-solution')],
      });
    },
  }),

  template({
    id: 'absolute-value-no-solution',
    conceptId: 'absolute-value',
    description: 'Recognise an absolute value equation with no solution.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const impossible = rng.bool();
      const k = rng.int(2, 15);
      const count = impossible ? 0 : 2;

      return numericExercise({
        prompt: `How many solutions does this equation have?`,
        latex: `|x| = ${impossible ? '-' : ''}${k}`,
        value: count,
        summary: impossible
          ? `An absolute value is a distance, so it can never be negative. There is no solution.`
          : `Two numbers are ${k} away from zero: ${k} and −${k}.`,
        hint: 'Absolute value measures distance from zero. Can a distance be negative?',
        steps: [
          {
            label: impossible ? 'The right-hand side is negative.' : 'The right-hand side is positive.',
            latex: impossible ? '\\text{no solution}' : `x = \\pm ${k}`,
          },
        ],
        traps: [
          ...(impossible
            ? [trap(2, 'absolute-value-negative-result'), trap(1, 'absolute-value-one-solution')]
            : [trap(1, 'absolute-value-one-solution')]),
        ],
      });
    },
  }),

  template({
    id: 'absolute-value-inequality-band',
    conceptId: 'absolute-value',
    description: 'Solve |x + p| < k, where the answer is a single band.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const k = rng.int(2, difficulty === 4 ? 15 : 8);
      const shift = difficulty === 4 ? rng.int(1, 9) * (rng.bool() ? 1 : -1) : 0;
      const closed = rng.bool();
      // |x + shift| < k holds between −k − shift and k − shift.
      const upper = k - shift;
      const lower = -k - shift;
      const wantUpper = rng.bool();
      const answer = wantUpper ? upper : lower;
      const inside = shift === 0 ? 'x' : `x ${shift < 0 ? '-' : '+'} ${Math.abs(shift)}`;
      const sign = closed ? '\\le' : '<';
      const plain = closed ? '≤' : '<';

      return numericExercise({
        prompt: `The solutions of this inequality form one band. What is its ${wantUpper ? 'upper' : 'lower'} endpoint?`,
        latex: `|${inside}| ${sign} ${k}`,
        value: answer,
        summary: `"Less than ${k} from zero" means the inside lies between −${k} and ${k}: −${k} ${plain} ${inside} ${plain} ${k}, so ${lower} ${plain} x ${plain} ${upper}.`,
        hint: 'Less than means near zero — one unbroken band. Write the inside between the two numbers and solve all three parts at once.',
        steps: [
          { label: 'The inside lies between the two numbers.', latex: `-${k} ${sign} ${inside} ${sign} ${k}` },
          { label: 'Solve every part at once.', latex: `${lower} ${sign} x ${sign} ${upper}` },
        ],
        traps: [
          /*
           * The second case negated in the number but not in the direction:
           * "x < k and x < −k", whose lower end is k + shift rather than
           * −k − shift. Suppressed when it lands on a real endpoint.
           */
          ...(k + shift === answer || k + shift === (wantUpper ? lower : upper)
            ? []
            : [trap(k + shift, 'absolute-value-second-case-not-reversed')]),
          // Only the first case solved, so the other endpoint is never found.
          ...(wantUpper ? [] : [trap(upper, 'absolute-value-one-solution')]),
        ],
      });
    },
  }),

  template({
    id: 'absolute-value-inequality-shape',
    conceptId: 'absolute-value',
    description: 'Decide whether an absolute value inequality gives one band or two pieces.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const k = rng.int(2, 12);
      const greater = rng.bool();
      const sign = greater ? '>' : '<';

      const { choices, correctId } = options(
        [
          greater
            ? { label: `x < -${k} or x > ${k}`, correct: true }
            : { label: `-${k} < x < ${k}`, correct: true },
          greater
            ? { label: `-${k} < x < ${k}`, misconceptionId: 'absolute-value-and-or-swapped' }
            : { label: `x < -${k} or x > ${k}`, misconceptionId: 'absolute-value-and-or-swapped' },
          { label: `x ${sign} ${k}`, misconceptionId: 'absolute-value-one-solution' },
          greater
            ? { label: `x > ${k} and x > -${k}`, misconceptionId: 'absolute-value-second-case-not-reversed' }
            : { label: `x < ${k} and x < -${k}`, misconceptionId: 'absolute-value-second-case-not-reversed' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which of these is the solution set?',
        latex: `|x| ${sign} ${k}`,
        figure: {
          kind: 'number-line',
          from: -Math.max(k + 3, 6),
          to: Math.max(k + 3, 6),
          step: Math.max(1, Math.round(k / 4)),
          marks: [
            { at: -k, label: `−${k}` },
            { at: k, label: `${k}` },
          ],
        },
        choices,
        correctId,
        summary: greater
          ? `"More than ${k} from zero" means far out in either direction, and nothing can be far out both ways at once — so it is two separate pieces, joined by "or".`
          : `"Less than ${k} from zero" means close to zero, so both conditions hold at the same time — one unbroken band between −${k} and ${k}.`,
        hint: `Read it as distance. Does "${greater ? 'more' : 'less'} than ${k} away from zero" describe the middle of the line, or the two ends of it?`,
      });
    },
  }),
];
